<template>
    <div class="stop-scrolling camera-container" v-if="photoData != null">
        <div class="background">
            <span class="loadingCameraText" v-if="hasCamera && !videoLive" style="position: absolute">
                Aguarde, inicializando a câmera...
            </span>

            <web-cam class="md-image" height="100%"
                ref="camera"
                screenshot-format="image/jpeg"
                :resolution="dimensions"
                @cameras="startCamera" @started="setTrack"
                @camera-change="afterChangeCamera"
                @video-live="() => videoLive = true"
                @stopped="() => videoLive = false"
                @error="fireErrorAlert"
                @notsupported="fireErrorAlert"
            />

            <div id="buttons" class="button-container">
                <div class="cameraButtons">
                    <button :class="{invisible: !hasTorch}" :disabled="!videoLive" id="flashButton" @click="toggleFlash">
                        <font-awesome-icon icon="bolt" size="lg"></font-awesome-icon>
                    </button>
                    <button
                        id="snapButton"
                        @click.passive="takePicture"
                        :disabled="!videoLive"
                    ></button>
                    <button id="changeCameraButton" @click.stop="$bvModal.show('cameraSelectModal')" :disabled="!canChangeCamera">
                        <font-awesome-icon icon="sync-alt" size="2x"></font-awesome-icon>
                        <font-awesome-icon icon="camera"></font-awesome-icon>
                    </button>
                </div>
            </div>
        </div>

        <div class="processing-overlay" v-show="showLoadingOverlay">
            <font-awesome-icon icon="sync-alt" size="3x" spin inverse></font-awesome-icon>
        </div>

        <b-modal id="cameraSelectModal" centered hide-footer>
                <template #modal-title>
                    <span class="text-primary">Selecione uma câmera</span>
                </template>
                <button-select :list="formattedCameraList" v-model="selectedFormattedCamera" @selected="onCameraSelect"></button-select>
        </b-modal>
        
    </div>
</template>

<script>
import Vue from "vue";
import { BModal, ModalPlugin } from "bootstrap-vue";

import { library } from "@fortawesome/fontawesome-svg-core";
import { faBolt, faSyncAlt, faCamera } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
library.add([faSyncAlt, faBolt, faCamera]);

import ButtonSelect from "../beforeInspection/buttonSelect.vue";

Vue.use(ModalPlugin);

import { WebCam } from "vue-web-cam";

import Swal from "sweetalert2";
import DeviceInfo from "@/controller/deviceInfo";

export default {
    props: {
        photoData: Object,
    },
    components: {
        "font-awesome-icon": FontAwesomeIcon,
        "button-select": ButtonSelect,
        WebCam,
        BModal
    },
    data: function () {
        return {
            cameras: [],
            formattedCameraList: [],
            selectedFormattedCamera: {},
            hasCamera: true,
            flashOn: false,
            camera: null,

            maxWidth: null,
            maxHeight: null,
            width: null,
            height: null,

            deviceId: null,
            track: null,
            videoLive: false,
            hasTorch: false,
            aspectRatio: 3/2,
            showLoadingOverlay: false,
            canChangeCamera: false
        };
    },
    computed: {
        dimensions: function () {
            return {height: this.height ?? 1080, width: this.width ?? 1620}; 
        }
    },
    methods: {
        fireErrorAlert(error) {
            console.error(error);
            Swal.fire({
                icon: "warning",
                title: "Não foi possível iniciar sua câmera",
                text: "Remova qualquer sobreposição de tela e tente novamente!",
                confirmButtonColor: "#3085d6",
                confirmButtonText: "Ok",
            });
        },

        isIos() {
            let deviceInfo = new DeviceInfo();
            return deviceInfo.isIos();
        },

        setTrack(e) {
            this.track = e.getVideoTracks()[0];
            const trackSettings = this.track.getSettings();
            this.hasTorch = Object.hasOwn(trackSettings, "torch");
        },

        checkFlashState() {
            return this.track.getSettings().torch;
        }, 

        toggleFlash() {
            if(this.track){
                this.flashOn = this.checkFlashState();
                if(this.flashOn) {
                    this.track.applyConstraints({
                        advanced: [{ torch: false }],
                    });
                    this.flashOn = false;
                } else {
                    this.track.applyConstraints({
                        advanced: [{ torch: true }],
                    });
                    this.flashOn = true;
                }
            }
        },

        checkPortrait() {
            return window.matchMedia("(orientation: portrait)").matches;
        },

        preventDefault(e) {
            e.preventDefault();
        },

        getFirstCameraIndex(cameras){
            let cameraNum = 0;
            if(!this.isIos()){
                cameraNum = cameras.length - 1;
            }
            return cameraNum;
        },

        async startCamera(e) {
            try {
                const cameras = e.filter(device => device.kind === "videoinput");
            
                let backFacingCameras = cameras.filter(camera => {
                    let isEnvironment = true;
                    if("getCapabilities" in camera){
                        const capabilites = camera.getCapabilities();
                        isEnvironment = capabilites?.facingMode.includes("environment");
                    } else {
                        const cameraLabel = camera.label.toLowerCase();
                        isEnvironment = cameraLabel.includes("back") || cameraLabel.includes("traseira");
                    }
                    return isEnvironment;
                });

                this.cameras = backFacingCameras;
                this.formattedCameraList = this.formatCameraList(backFacingCameras);

                let acceptableFCameras = backFacingCameras.filter(camera => {
                    let acceptableFocusDistance = false;
                    if("getCapabilities" in camera){
                        const capabilites = camera.getCapabilities();
                        acceptableFocusDistance = (capabilites?.focusDistance?.min ?? 0) < 0.5;
                    }
                    return acceptableFocusDistance;
                });
            
                let selectedCamera = null;
                const lastSelectedCameraId = this.$store.state.options.lastSelectedCameraId;

                if(lastSelectedCameraId != null) {
                    selectedCamera = this.getCameraByDeviceId(lastSelectedCameraId);
                }
            
                if(!selectedCamera) {
                // No android, a última câmera costuma ter o flash disponível
                    if(acceptableFCameras && acceptableFCameras.length > 0){
                        selectedCamera = acceptableFCameras[this.getFirstCameraIndex(acceptableFCameras)];
                    } else if(backFacingCameras && backFacingCameras.length > 0) {
                        selectedCamera = backFacingCameras[this.getFirstCameraIndex(backFacingCameras)];
                    } else {
                        selectedCamera = cameras[this.getFirstCameraIndex(cameras)];
                    }
                }

                this.camera = selectedCamera;
                this.selectedFormattedCamera = this.formatCamera(selectedCamera);
                this.changeCamera(selectedCamera);
            } catch (error) {
                console.error(error);
                await Swal.fire({
                    icon: "error",
                    title: "Erro ao inicializar câmera",
                    html:
                        "<span>Algum erro impediu o carregamento da câmera.</span>" +
                        "<details style='text-align: left;'>" +
                        "   <summary class='p-1' style='text-align: left; background-color: #f7f7f7; border-radius: 4px'>Detalhes</summary>" +
                        "   <pre style='overflow: auto; text-align: left; border: 1px solid #919191; border-radius: 4px'>" + 
                        //"       <code>" + error.stack + "</code>" + 
                        "       <code>" + error.message + "</code>" +
                        "   </pre>" +
                        "</details>",
                    confirmButtonColor: "#3085d6",
                    confirmButtonText: "Voltar",
                });
                this.$router.go(-1);
            }
        },

        changeCamera(camera) {
            try{
                if(!camera) {
                    Swal.fire({
                        icon: "error",
                        title: "Erro ao escolher câmera",
                        text: "A câmera selecionada não está disponível. Tente escolher outra.",
                        confirmButtonColor: "#3085d6",
                        confirmButtonText: "Fechar",
                    });
                    return;
                }

                if("getCapabilities" in camera){
                    const cameraCapabilities = camera.getCapabilities();
                    this.height = Math.min(1080, cameraCapabilities.height.max);
                    this.width = this.height*(3/2);

                    this.maxWidth = cameraCapabilities.width.max;
                    this.maxHeight = cameraCapabilities.height.max;
                } else {
                    this.height = 1080;
                    this.width = this.height*(3/2);

                    this.maxWidth = this.width;
                    this.maxHeight = this.height;
                }
                
                /* Camera com resolução máxima */
                //this.width = this.maxWidth;
                //this.height = Math.floor(this.width / this.aspectRatio);
                //this.$refs.camera.resolution = this.dimensions;

                this.$refs.camera.changeCamera(camera.deviceId);
            } catch(error) {
                console.error(error);
                Swal.fire({
                    icon: "error",
                    title: "Erro ao escolher câmera",
                    text: "Algum erro impediu a inicialização da câmera selecionada",
                    confirmButtonColor: "#3085d6",
                    confirmButtonText: "Fechar",
                });
                this.canChangeCamera = true;
            }
        },

        afterChangeCamera(deviceId) {
            this.canChangeCamera = true;
            if(deviceId !== this.$store.state.options.lastSelectedCameraId){
                this.$store.commit("setLastSelectedCameraId", deviceId ?? null);
                localStorage.setItem("lastSelectedCameraId", JSON.stringify(deviceId ?? null));
            }
        },

        onCameraSelect(e) {
            this.canChangeCamera = false;
            this.$bvModal.hide("cameraSelectModal");
            this.changeCamera(this.getCameraByDeviceId(e.deviceId));
        },

        formatCamera(camera) {
            return {
                title: camera.label,
                selected: false,
                deviceId: camera.deviceId
            };
        },

        formatCameraList(cameras) {
            return cameras.map(camera => this.formatCamera(camera));
        },

        getCameraByDeviceId(deviceId) {
            return this.cameras.find(camera => camera.deviceId === deviceId);
        },

        takePicture: async function () {
            if(this.videoLive){
                try {
                    this.$refs.camera.pause();
                    const canvas = this.$refs.camera.getCanvas();
                    const tokenEnterprise = this.$store.getters.allInspectionData[0].enterprise;
                    const inspectionType =
                        parseInt(this.$store.state.inspectionData.operation.tipo_vistoria) === 1 // Tipo: entrega
                            ? "ENT"
                            : "COL";
                            
                    const currentDate = new Date();
                    const fullDate = currentDate.toLocaleString("pt-BR").replaceAll(",", "");
                    let nameDate = fullDate.replaceAll(/(\/|:|,)/g, "-");

                    let actualDimensions = this.dimensions;
                    const viewportIsPortrait = window.matchMedia("(orientation: portrait)").matches;
                    if (viewportIsPortrait) {
                        actualDimensions = {
                            width: this.dimensions.height,
                            height: this.dimensions.width
                        };
                    }

                    this.showLoadingOverlay = true;
                    const originalPhotoBase64 = canvas.toDataURL("image/jpeg", 100);
                    
                    const photo = {
                        selected: false,
                        template_id: this.photoData.template_id,
                        timestamp: fullDate.replace(/:[^:]+$/, ""),
                        ordem: this.photoData.ordem ? this.photoData.ordem : 1,
                        photoName: this.photoData.photoName,
                        image_name: `${tokenEnterprise}_${this.photoData.photoName}
                            _${this.photoData.ordem ? this.photoData.ordem : 1}
                            _${nameDate}_${inspectionType}`.replace(/[\s_]+/g, "_"),
                        image_base64: originalPhotoBase64,
                    };

                    this.$store.commit("addPhotoNoEdit", originalPhotoBase64);
                    this.$store.commit("setSizePhoto", actualDimensions);
                    this.showLoadingOverlay = false;
                    this.$router.push({
                        name: "editPhoto",
                        params: { photo: photo }
                    });
                } catch (error) {
                    console.error(error);
                    Swal.fire({
                        icon: "error",
                        title: "Erro ao tirar foto",
                        text: "Ocorreu algum erro ao tentar registrar a foto",
                        confirmButtonColor: "#3085d6",
                        confirmButtonText: "Fechar",
                    });
                } finally {
                    this.showLoadingOverlay = false;
                    this.$refs.camera.resume();
                }
                
            } else {
                console.error("Câmera não está streamando");
                Swal.fire({
                    icon: "warning",
                    title: "Não foi possível iniciar sua câmera",
                    text: "Remova qualquer sobreposição de tela e tente novamente!",
                    confirmButtonColor: "#3085d6",
                    confirmButtonText: "Ok",
                });
            }
        },
    },

    created: function() {
        if(this.photoData == null){
            this.$router.go(-1);
            return;
        }
        
        if(!this.$store.state.options.lastSelectedCameraId){
            const lastSelectedCameraId = JSON.parse(localStorage.getItem("lastSelectedCameraId"));
            this.$store.commit("setLastSelectedCameraId", lastSelectedCameraId);
        }
    },

    beforeDestroy() {
        if(this.track) {
            this.$refs.camera.stop();
            if(this.track != null){
                this.track.stop();
            }
        }
    },
};
</script>

<style scoped lang="scss">

#header, #navbar {
    display: none;
}

video {
    max-width: initial;
}

video.md-image {
    display: block;
}

.camera-container{
    position: fixed;
    display: flex;
    z-index: 200;
    height: 100%;
}
.background {
    width: 100vw;
    height: 100%;
    background-color: #98e6c3;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
}

/*#camera, .loadingCameraText {
    position: absolute;
    top: 50%;
    transform: translate(0, -50%);
}*/

#camera {
    z-index: 210;
}

.loadingCameraText {
    z-index: 200;
    text-align: center;
    color: rgb(73, 73, 119);
    font-size: larger;
    background-color: #f4fde8;
    padding: 15px;
    border-radius: 8px;
    border: 1px solid #B0D080;
}

.button-container {
    display: flex;
    bottom: 20px;
    position: absolute;
    width: 100%;
    justify-content: center;
    align-items: center;
}

.cameraButtons {
    z-index: 300;
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    align-items: center;
    height: fit-content;
}

#snapButton, #flashButton, #changeCameraButton {
    border: 2px solid rgba(107, 103, 112, 0.25);

    &:not(:disabled):active {
        border: 2px solid rgba(107, 103, 112, 0.8);
        box-shadow: inset 0px 0px 0px 1px rgba(27, 27, 36, 0.295);
    }
}

#snapButton {
    height: 60px;
    width: 60px;
    background-color: white;
    border-radius: 50%;

    &:not(:disabled):active {
        background-color: rgb(248, 248, 248);
    }

    &:disabled {
        opacity: 65%;
    }
}

#flashButton, #changeCameraButton {
    height: 50px;
    width: 50px;
    border-radius: 50%;
    user-select: none;
    -webkit-user-select: none;
    -webkit-user-drag: none;

    .invisible {
        pointer-events: none;
    }

    &:disabled {
        opacity: 65%;
    }
}

#flashButton {
    background-color: #83af9f;

    svg[class*="fa-"] {
        color: #fcd931;
    }
}

#changeCameraButton {
    position: relative;
    background-color: #83af9f;

    &:disabled {
        svg[class*="fa-"] {
            color: rgb(214, 214, 214);
        }
    }
    
    &> * {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
    }

    svg[class*="fa-"] {
        color: white;

        &:is(.fa-sync-alt,.fa-camera)> path {
            stroke: #83af9f !important;
        }

        &.fa-sync-alt > path {
            stroke-width: 5% !important;
            stroke-linecap: round !important;
        }

        &.fa-camera > path {
            stroke-width: 3% !important;
        }
    }
}

.processing-overlay {
    position: fixed;
    background-color: rgba(255, 255, 255, 0.4);
    height: 100%;
    width: 100vw;
    display: flex;
    z-index: 350;
    * {
        margin: auto auto;    
    }
}

@media screen and (orientation: landscape) {
    .button-container {
        bottom: initial;
        right: 40px;
        width: initial;
        height: 100%;

        .cameraButtons {
            margin-top: 0;
            flex-direction: column-reverse;
            align-items: center;
            width: initial;
            height: 100%;
        }
    }
}
</style>
